/**
 * @internal
 */

import type {
  FingerprintData,
  FontFingerprintData,
  WebRTCFingerprintData,
  SensorFingerprintData,
  AdvancedBrowserData,
  HardwareData,
  PermissionsData
} from '../types';
import { detectFonts } from '../utils/fontDetector';
import { simpleHash } from '../utils/hash';

export class FingerprintCollector {
  private readonly fingerprintEndpoint: string;
  private readonly apiKey?: string;

  constructor(fingerprintEndpoint: string = 'https://fingerprint.ipranker.com/fingerprint-visitor-id', apiKey?: string) {
    this.fingerprintEndpoint = fingerprintEndpoint;
    this.apiKey = apiKey;
  }

  async collect(): Promise<FingerprintData> {
    try {
      const completeDeviceData = await this.collectCompleteDeviceData();

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };

      if (this.apiKey) {
        headers['x-api-key'] = this.apiKey;
      }

      const response = await fetch(this.fingerprintEndpoint, {
        method: 'POST',
        headers,
        credentials: 'omit',
        body: JSON.stringify({ deviceData: completeDeviceData })
      });

      if (!response.ok) {
        return await this.collectBasicFingerprint();
      }

      const fingerprintData = await response.json();

      if (!fingerprintData.visitorId && !fingerprintData.fingerprints?.installationId) {
        return await this.collectBasicFingerprint();
      }

      return this.transformFingerprintResponse(fingerprintData);

    } catch (error) {
      return await this.collectBasicFingerprint();
    }
  }

  private transformFingerprintResponse(data: any): FingerprintData {
    const baseFingerprint: FingerprintData = {
      installationId: data.visitorId || data.fingerprints?.installationId || data.installationId || null,
      canvas: {
        text: data.fingerprints?.canvasText || data.canvas?.text || null,
        geometry: data.fingerprints?.canvasGeometry || data.canvas?.geometry || null,
        blending: data.fingerprints?.canvasBlending || data.canvas?.blending || null
      },
      webgl: data.fingerprints?.webgl || data.webgl ? {
        vendor: (data.fingerprints?.webgl?.vendor || data.webgl?.vendor) || null,
        renderer: (data.fingerprints?.webgl?.renderer || data.webgl?.renderer) || null,
        extensions: (data.fingerprints?.webgl?.extensions?.length || data.webgl?.extensions?.length || 0)
      } : { vendor: null, renderer: null, extensions: 0 },
      audio: data.fingerprints?.audio || data.audio || null,
      screen: {
        width: data.screen?.width || window.screen.width,
        height: data.screen?.height || window.screen.height,
        colorDepth: data.screen?.colorDepth || window.screen.colorDepth,
        pixelRatio: data.screen?.pixelRatio || window.devicePixelRatio,
        orientation: data.screen?.orientation || screen.orientation?.type || (window as any).orientation || 'unknown'
      },
      timezone: data.localization?.timeZone || data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: data.localization?.languages?.[0] || data.language || navigator.language,
      platform: data.platform?.platform || data.platform || navigator.platform
    };

    if (data.fonts) baseFingerprint.fonts = data.fonts;
    if (data.webrtc) baseFingerprint.webrtc = data.webrtc;
    if (data.sensors) baseFingerprint.sensors = data.sensors;
    if (data.advancedBrowser) baseFingerprint.advancedBrowser = data.advancedBrowser;
    if (data.hardware) baseFingerprint.hardware = data.hardware;
    if (data.permissions) baseFingerprint.permissions = data.permissions;

    return baseFingerprint;
  }

  private async collectBasicFingerprint(): Promise<FingerprintData> {
    const [fontData, webrtcData] = await Promise.all([
      this.collectFontFingerprint(),
      this.collectWebRTCFingerprint()
    ]);

    const sensorData = this.collectSensorFingerprint();
    const advancedBrowserData = this.collectAdvancedBrowserFeatures();
    const hardwareData = this.collectHardwareData();
    const permissionsData = await this.collectPermissionsState();
    const webglData = this.getWebGLFingerprint();

    return {
      installationId: null,
      canvas: {
        text: this.getCanvasFingerprint(),
        geometry: this.getCanvasGeometryFingerprint(),
        blending: this.getCanvasBlendingFingerprint()
      },
      webgl: webglData ? {
        vendor: webglData.vendor || null,
        renderer: webglData.renderer || null,
        extensions: webglData.extensions || 0
      } : { vendor: null, renderer: null, extensions: 0 },
      audio: null,
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        colorDepth: window.screen.colorDepth,
        pixelRatio: window.devicePixelRatio,
        orientation: screen.orientation?.type || (window as any).orientation || 'unknown'
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      fonts: fontData,
      webrtc: webrtcData,
      sensors: sensorData,
      advancedBrowser: advancedBrowserData,
      hardware: hardwareData,
      permissions: permissionsData
    };
  }

  private async collectCompleteDeviceData(): Promise<any> {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;

    const [fontData, webrtcData] = await Promise.all([
      this.collectFontFingerprint(),
      this.collectWebRTCFingerprint()
    ]);

    const sensorData = this.collectSensorFingerprint();
    const advancedBrowserData = this.collectAdvancedBrowserFeatures();
    const hardwareData = this.collectHardwareData();
    const permissionsData = await this.collectPermissionsState();
    const webglData = this.getWebGLFingerprint();

    return {
      platform: {
        brands: (navigator as any).userAgentData?.brands || [],
        platform: platform,
        mobile: /Mobile|Android|iPhone|iPad|iPod/i.test(userAgent),
        architecture: (navigator as any).userAgentData?.architecture || 'unknown',
        bitness: (navigator as any).userAgentData?.bitness || 'unknown',
        browserInfo: {
          userAgent: userAgent,
          language: navigator.language,
          languages: navigator.languages || [navigator.language]
        }
      },
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        colorDepth: window.screen.colorDepth,
        pixelDepth: window.screen.pixelDepth || window.screen.colorDepth,
        orientation: screen.orientation?.type || (window as any).orientation || 'portrait-primary',
        pixelRatio: window.devicePixelRatio || 1
      },
      hardware: hardwareData,
      capabilities: {
        cookiesEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack === '1' || (window as any).doNotTrack === '1',
        pdfViewerEnabled: (navigator as any).pdfViewerEnabled ?? true
      },
      localization: {
        languages: navigator.languages || [navigator.language],
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      fingerprints: {
        canvasText: this.getCanvasFingerprint(),
        canvasBlending: this.getCanvasBlendingFingerprint(),
        canvasGeometry: this.getCanvasGeometryFingerprint(),
        webgl: webglData,
        timingResolution: typeof performance !== 'undefined' && typeof performance.now === 'function' ? 'high' : 'low'
      },
      fonts: { fontCount: fontData.fontCount, fontFingerprint: fontData.fontFingerprint },
      webrtc: webrtcData,
      sensors: sensorData,
      advancedBrowser: advancedBrowserData,
      permissions: permissionsData
    };
  }

  private async collectFontFingerprint(): Promise<FontFingerprintData> {
    try {
      return await detectFonts();
    } catch {
      return { installedFonts: [], fontCount: 0, fontFingerprint: 'error', detectionTimeMs: 0 };
    }
  }

  private async collectWebRTCFingerprint(): Promise<WebRTCFingerprintData> {
    const result: WebRTCFingerprintData = {
      webrtcSupported: !!window.RTCPeerConnection,
      mediaDevicesCount: 0,
      hasCamera: false,
      hasMicrophone: false,
      hasSpeaker: false,
      webrtcFingerprint: ''
    };

    try {
      if (navigator.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        result.mediaDevicesCount = devices.length;
        result.hasCamera = devices.some(d => d.kind === 'videoinput');
        result.hasMicrophone = devices.some(d => d.kind === 'audioinput');
        result.hasSpeaker = devices.some(d => d.kind === 'audiooutput');
      }
    } catch { /* silent */ }

    result.webrtcFingerprint = simpleHash(JSON.stringify({
      supported: result.webrtcSupported,
      devices: result.mediaDevicesCount,
      camera: result.hasCamera,
      mic: result.hasMicrophone,
      speaker: result.hasSpeaker
    }));

    return result;
  }

  private collectSensorFingerprint(): SensorFingerprintData {
    const result: SensorFingerprintData = {
      accelerometerSupported: 'Accelerometer' in window,
      gyroscopeSupported: 'Gyroscope' in window,
      magnetometerSupported: 'Magnetometer' in window,
      ambientLightSupported: 'AmbientLightSensor' in window,
      sensorFingerprint: ''
    };

    result.sensorFingerprint = simpleHash(JSON.stringify({
      accel: result.accelerometerSupported,
      gyro: result.gyroscopeSupported,
      mag: result.magnetometerSupported,
      light: result.ambientLightSupported
    }));

    return result;
  }

  private collectAdvancedBrowserFeatures(): AdvancedBrowserData {
    const result: AdvancedBrowserData = {
      localStorageEnabled: this.isStorageAvailable('localStorage'),
      sessionStorageEnabled: this.isStorageAvailable('sessionStorage'),
      indexedDBEnabled: !!window.indexedDB,
      cacheAPIEnabled: 'caches' in window,
      webGL2Supported: !!document.createElement('canvas').getContext('webgl2'),
      webAssemblySupported: typeof WebAssembly === 'object',
      serviceWorkerSupported: 'serviceWorker' in navigator,
      doNotTrack: navigator.doNotTrack === '1' || (window as any).doNotTrack === '1',
      pdfViewerEnabled: (navigator as any).pdfViewerEnabled ?? true,
      browserFeaturesFingerprint: ''
    };

    result.browserFeaturesFingerprint = simpleHash(JSON.stringify({
      ls: result.localStorageEnabled,
      ss: result.sessionStorageEnabled,
      idb: result.indexedDBEnabled,
      cache: result.cacheAPIEnabled,
      webgl2: result.webGL2Supported,
      wasm: result.webAssemblySupported,
      sw: result.serviceWorkerSupported,
      dnt: result.doNotTrack,
      pdf: result.pdfViewerEnabled
    }));

    return result;
  }

  private isStorageAvailable(type: 'localStorage' | 'sessionStorage'): boolean {
    try {
      const storage = window[type];
      const testKey = '__t__';
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private collectHardwareData(): HardwareData {
    const webglData = this.getWebGLFingerprint();
    return {
      cores: navigator.hardwareConcurrency || 4,
      memory: (navigator as any).deviceMemory || null,
      maxTouchPoints: navigator.maxTouchPoints || 0,
      gpuVendor: webglData?.vendor || null,
      gpuRenderer: webglData?.renderer || null
    };
  }

  private async collectPermissionsState(): Promise<PermissionsData> {
    const result: PermissionsData = { geolocation: 1, notifications: 1, camera: 1, microphone: 1 };

    const encodeState = (state: string): number => {
      switch (state) {
        case 'granted': return 2;
        case 'prompt': return 1;
        case 'denied': return 0;
        default: return 1;
      }
    };

    try {
      if (navigator.permissions) {
        const names = ['geolocation', 'notifications', 'camera', 'microphone'] as const;
        for (const name of names) {
          try {
            const status = await navigator.permissions.query({ name: name as PermissionName });
            result[name] = encodeState(status.state);
          } catch { /* unsupported */ }
        }
      }
    } catch { /* silent */ }

    return result;
  }

  private getCanvasFingerprint(): string | null {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      canvas.width = 240;
      canvas.height = 60;

      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = '#f60';
      ctx.fillRect(100, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.font = '11pt Arial';
      ctx.fillText('Cwm fjordbank glyphs vext quiz, 😃', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.font = '18pt Arial';
      ctx.fillText('Cwm fjordbank glyphs vext quiz, 😃', 4, 45);

      return canvas.toDataURL();
    } catch {
      return null;
    }
  }

  private getCanvasBlendingFingerprint(): string | null {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      canvas.width = 100;
      canvas.height = 100;

      ctx.fillStyle = 'rgb(255,0,255)';
      ctx.beginPath();
      ctx.arc(50, 50, 50, 0, Math.PI * 2, true);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = 'rgb(0,255,255)';
      ctx.globalCompositeOperation = 'multiply';
      ctx.beginPath();
      ctx.arc(25, 25, 25, 0, Math.PI * 2, true);
      ctx.closePath();
      ctx.fill();

      return canvas.toDataURL();
    } catch {
      return null;
    }
  }

  private getCanvasGeometryFingerprint(): string | null {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      canvas.width = 122;
      canvas.height = 110;

      ctx.globalCompositeOperation = 'multiply';
      for (let i = 0; i < 20; i++) {
        ctx.fillStyle = `rgb(${Math.floor(255 - i * 10)}, ${Math.floor(i * 10)}, ${Math.floor(i * 5)})`;
        ctx.beginPath();
        ctx.arc(i * 5, i * 5, i + 10, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.fill();
      }

      return canvas.toDataURL();
    } catch {
      return null;
    }
  }

  private getWebGLFingerprint(): any {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext;
      if (!gl) return null;

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

      return {
        vendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
        renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
        version: gl.getParameter(gl.VERSION),
        shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
        extensions: gl.getSupportedExtensions()?.length || 0
      };
    } catch {
      return null;
    }
  }
}
